import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider,
  Chip,
  IconButton,
  TextField
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckIcon from '@mui/icons-material/Check';
import {
  useCampaignContextController,
  updateCurrentCampaign
} from '../../context/CampaignContext';
import { formatLocaleDateTime } from '../../utils/DateTimeFormatter';
import AvatarGenerator from '../avatar/avatar';
import { CampaignProspectType, updateCampaign } from '../../api/campaigns';
import SidebarPanelHeader from './SidebarPanelHeader';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSnackbar } from '../../context/SnackbarContext';

interface CampaignSettingsProps {
  onClose: () => void;
}

const labelStyles = {
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.5px',
  mb: 1,
  display: 'block'
};

const CampaignSettings: React.FC<CampaignSettingsProps> = ({ onClose }) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [campaignState, dispatch] = useCampaignContextController();
  const { currentCampaign, canEdit } = campaignState;
  const { showSnackbar } = useSnackbar();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [localName, setLocalName] = useState(currentCampaign?.name ?? '');
  const [localDescription, setLocalDescription] = useState(
    currentCampaign?.description ?? ''
  );
  const [isSavingName, setIsSavingName] = useState(false);
  const [justSavedName, setJustSavedName] = useState(false);
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [justSavedDescription, setJustSavedDescription] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);
  const savedNameTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const savedDescriptionTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  useEffect(() => {
    return () => {
      if (savedNameTimeoutRef.current)
        clearTimeout(savedNameTimeoutRef.current);
      if (savedDescriptionTimeoutRef.current)
        clearTimeout(savedDescriptionTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (currentCampaign && !isEditingName && !isEditingDescription) {
      setLocalName(currentCampaign.name);
      setLocalDescription(currentCampaign.description ?? '');
    }
  }, [
    currentCampaign?.name,
    currentCampaign?.description,
    isEditingName,
    isEditingDescription
  ]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
      descriptionInputRef.current.select();
    }
  }, [isEditingDescription]);

  const handleSaveName = useCallback(async () => {
    setIsEditingName(false);
    if (!campaignId || !currentCampaign) return;
    const trimmed = localName.trim();
    if (!trimmed || trimmed === currentCampaign.name) {
      setLocalName(currentCampaign.name);
      return;
    }
    setIsSavingName(true);
    setJustSavedName(false);
    try {
      const updated = await updateCampaign(campaignId, { name: trimmed });
      updateCurrentCampaign(dispatch, updated);
      setIsSavingName(false);
      setJustSavedName(true);
      if (savedNameTimeoutRef.current)
        clearTimeout(savedNameTimeoutRef.current);
      savedNameTimeoutRef.current = setTimeout(
        () => setJustSavedName(false),
        1000
      );
    } catch {
      setIsSavingName(false);
      setJustSavedName(false);
      setLocalName(currentCampaign.name);
      showSnackbar('Failed to update campaign name', 'error');
    }
  }, [campaignId, currentCampaign, localName, dispatch, showSnackbar]);

  const handleCancelName = useCallback(() => {
    setIsEditingName(false);
    setLocalName(currentCampaign?.name ?? '');
  }, [currentCampaign?.name]);

  const handleSaveDescription = useCallback(async () => {
    setIsEditingDescription(false);
    if (!campaignId || !currentCampaign) return;
    const trimmed = localDescription.trim();
    const currentDesc = currentCampaign.description ?? '';
    if (trimmed === currentDesc) {
      setLocalDescription(currentDesc);
      return;
    }
    setIsSavingDescription(true);
    setJustSavedDescription(false);
    try {
      const updated = await updateCampaign(campaignId, {
        description: trimmed || null
      });
      updateCurrentCampaign(dispatch, updated);
      setIsSavingDescription(false);
      setJustSavedDescription(true);
      if (savedDescriptionTimeoutRef.current)
        clearTimeout(savedDescriptionTimeoutRef.current);
      savedDescriptionTimeoutRef.current = setTimeout(
        () => setJustSavedDescription(false),
        1000
      );
    } catch {
      setIsSavingDescription(false);
      setJustSavedDescription(false);
      setLocalDescription(currentDesc);
      showSnackbar('Failed to update description', 'error');
    }
  }, [campaignId, currentCampaign, localDescription, dispatch, showSnackbar]);

  const handleCancelDescription = useCallback(() => {
    setIsEditingDescription(false);
    setLocalDescription(currentCampaign?.description ?? '');
  }, [currentCampaign?.description]);

  if (!currentCampaign) {
    return (
      <Box>
        <SidebarPanelHeader
          icon={<SettingsIcon />}
          title="Campaign Settings"
          titleVariant="custom"
          actionButtons={[
            <IconButton
              key="close"
              size="small"
              onClick={onClose}
              aria-label="Close page"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ]}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No campaign selected.
        </Typography>
      </Box>
    );
  }

  // Determine prospect type with fallback logic
  const prospectType =
    currentCampaign.prospect_type ||
    (currentCampaign.name.toLowerCase().includes('account')
      ? CampaignProspectType.ACCOUNT
      : CampaignProspectType.OPPORTUNITY);

  const isAccountCampaign = prospectType === CampaignProspectType.ACCOUNT;
  const campaignTypeLabel = isAccountCampaign ? 'Account' : 'Opportunity';
  const ProspectIcon = isAccountCampaign ? BusinessIcon : AssignmentIcon;

  const fieldUnderlineColor = (theme: Theme) =>
    isAccountCampaign
      ? theme.customColors.spreadsheetAccountAccent
      : theme.customColors.themePrimary;

  return (
    <Box>
      <SidebarPanelHeader
        icon={<SettingsIcon />}
        title="Campaign Settings"
        titleVariant="custom"
        actionButtons={[
          <IconButton
            key="close"
            size="small"
            onClick={onClose}
            aria-label="Close page"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ]}
      />

      {/* Campaign Name */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          <Typography variant="caption" sx={labelStyles}>
            Campaign Name
          </Typography>
          {isSavingName && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
              aria-live="polite"
            >
              Saving…
            </Typography>
          )}
          {justSavedName && !isSavingName && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              aria-live="polite"
            >
              <CheckIcon sx={{ fontSize: 14 }} color="primary" />
              <Typography variant="caption" color="primary">
                Saved
              </Typography>
            </Box>
          )}
        </Box>
        {isEditingName ? (
          <TextField
            inputRef={nameInputRef}
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={handleSaveName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveName();
              if (e.key === 'Escape') handleCancelName();
            }}
            variant="standard"
            fullWidth
            size="small"
            sx={{
              '& .MuiInput-root': {
                '&:before': {
                  borderBottomColor: fieldUnderlineColor
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottomColor: fieldUnderlineColor
                },
                '&:after': {
                  borderBottomColor: fieldUnderlineColor
                }
              },
              '& .MuiInput-input': { fontSize: '1rem', fontWeight: 500 }
            }}
          />
        ) : (
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              cursor: canEdit ? 'pointer' : 'default',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              '&:hover': canEdit
                ? { backgroundColor: 'action.hover' }
                : undefined
            }}
            onClick={() => canEdit && setIsEditingName(true)}
          >
            {currentCampaign.name}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Prospect Type */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
            mb: 1,
            display: 'block'
          }}
        >
          Prospect Type
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ProspectIcon
            sx={{
              fontSize: '18px',
              color: 'text.secondary'
            }}
          />
          <Chip
            label={campaignTypeLabel}
            size="small"
            sx={{
              fontWeight: 500
            }}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Description */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1
          }}
        >
          <Typography variant="caption" sx={labelStyles}>
            Description
          </Typography>
          {isSavingDescription && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
              aria-live="polite"
            >
              Saving…
            </Typography>
          )}
          {justSavedDescription && !isSavingDescription && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              aria-live="polite"
            >
              <CheckIcon sx={{ fontSize: 14 }} color="primary" />
              <Typography variant="caption" color="primary">
                Saved
              </Typography>
            </Box>
          )}
        </Box>
        {isEditingDescription ? (
          <TextField
            inputRef={descriptionInputRef}
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            onBlur={handleSaveDescription}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleCancelDescription();
            }}
            variant="standard"
            fullWidth
            multiline
            minRows={2}
            placeholder="Add a description..."
            sx={{
              '& .MuiInput-root': {
                '&:before': {
                  borderBottomColor: fieldUnderlineColor
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottomColor: fieldUnderlineColor
                },
                '&:after': {
                  borderBottomColor: fieldUnderlineColor
                }
              },
              '& .MuiInput-input': {
                fontSize: '0.875rem',
                color: 'text.secondary'
              }
            }}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              cursor: canEdit ? 'pointer' : 'default',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              minHeight: 24,
              '&:hover': canEdit
                ? { backgroundColor: 'action.hover' }
                : undefined
            }}
            onClick={() => canEdit && setIsEditingDescription(true)}
          >
            <DescriptionIcon
              sx={{
                fontSize: '18px',
                color: 'text.secondary',
                mt: 0.5,
                flexShrink: 0
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: currentCampaign.description
                  ? 'text.secondary'
                  : 'text.disabled',
                fontStyle: currentCampaign.description ? 'inherit' : 'italic'
              }}
            >
              {currentCampaign.description ||
                (canEdit ? 'Add a description...' : 'No description')}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Owner */}
      {currentCampaign.creator_name && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.5px',
              mb: 1,
              display: 'block'
            }}
          >
            Owner
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AvatarGenerator
              fullname={currentCampaign.creator_name}
              size={32}
            />
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {currentCampaign.creator_name}
            </Typography>
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Created Date */}
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
            mb: 1,
            display: 'block'
          }}
        >
          Created
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon
            sx={{
              fontSize: '18px',
              color: 'text.secondary'
            }}
          />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {formatLocaleDateTime(currentCampaign.create_ts)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CampaignSettings;
